import React from "react";
import { Box, Flex, Icon, Link, Stack, Text, useColorModeValue } from "@chakra-ui/react";

/**
 * Circular icon badge for a social link.
 * @param {Object} props
 * @param {React.ElementType} props.icon - react-icons component
 * @param {string} props.color - Badge accent color
 * @returns {React.ReactElement}
 */
function SocialIconBadge({ icon, color }) {
    return (
        <Flex align="center" justify="center" w="40px" h="40px" borderRadius="50%"
            bg={`${color}20`} border={`1px solid ${color}40`} flexShrink="0">
            <Icon as={icon} color={color} w="20px" h="20px" />
        </Flex>
    );
}

/**
 * Single social media link row with icon, name, and URL.
 * @param {Object} props
 * @param {Object} props.link - Link object with name, url, icon component, and color
 * @returns {React.ReactElement}
 */
function SocialLinkRow({ link }) {
    const bg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    const nameColor = useColorModeValue("secondaryGray.900", "white");
    return (
        <Flex align="center" gap="14px" p="14px" bg={bg} borderRadius="12px">
            <SocialIconBadge icon={link.icon} color={link.color} />
            <Box flex="1" minW="0">
                <Text fontSize="sm" fontWeight="600" color={nameColor} mb="2px">{link.name}</Text>
                <Link href={link.url} target="_blank" rel="noopener noreferrer"
                    color="brand.500" fontSize="xs" wordBreak="break-all">
                    {link.url}
                </Link>
            </Box>
        </Flex>
    );
}

/**
 * Displays a list of social media links with icons and clickable URLs.
 * @param {Object} props
 * @param {Array} props.socialLinks - Array of link objects with name, url, icon, color
 * @returns {React.ReactElement}
 */
function SocialLinksDisplay({ socialLinks }) {
    const emptyBg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    const emptyColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    if (!socialLinks.length) {
        return (
            <Box p="16px" bg={emptyBg} borderRadius="12px" textAlign="center">
                <Text fontSize="sm" color={emptyColor}>Aucun réseau social renseigné</Text>
            </Box>
        );
    }
    return (
        <Stack spacing="12px">
            {socialLinks.map((link, index) => <SocialLinkRow key={index} link={link} />)}
        </Stack>
    );
}

export default SocialLinksDisplay;
